import React, { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon, SparklesIcon, XIcon, ChevronRightIcon, FilterIcon, LayoutListIcon, LayoutGridIcon } from 'lucide-react';
import { ProfileCard } from '../components/Directory/ProfileCard';
import { mapAuthorToProfileCard } from '../utils/profileCardMappers';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import AuthorCard from '../components/Authors/AuthorCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Extended Author type
interface Author {
  slug: string;
  name: string;
  avatar: string;
  bio: string;
  location: string;
  genres: string[];
  bookCount: number;
  rating: number;
  reviewCount?: number;
  hook?: string;
  readerExperience?: string[];
  badges?: string[];
  isDebut?: boolean;
  joinedDate?: string;
}
// Mock data for demonstration
const authors: Author[] = [{
  slug: 'elena-richards',
  name: 'Elena Richards',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Award-winning author of psychological thrillers and mystery novels. Known for intricate plots and complex female protagonists.',
  location: 'Seattle, WA',
  genres: ['Mystery', 'Thriller', 'Psychological Suspense'],
  bookCount: 12,
  rating: 4.7,
  reviewCount: 340,
  hook: 'Intricate psychological thrillers with complex female protagonists and shocking twists.',
  readerExperience: ['Unreliable Narrators', 'Complex Female Leads', 'Shocking Twists'],
  badges: ['Completed Series', 'Audiobooks Available'],
  joinedDate: '2023-01-15'
}, {
  slug: 'marcus-chen',
  name: 'Marcus Chen',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Fantasy and young adult fiction author, creating immersive worlds filled with magic and adventure.',
  location: 'Portland, OR',
  genres: ['Fantasy', 'Young Adult', 'Urban Fantasy'],
  bookCount: 8,
  rating: 4.5,
  reviewCount: 180,
  hook: 'Immersive fantasy worlds filled with magic, adventure, and found family.',
  readerExperience: ['Rich World-Building', 'Found Family', 'Coming of Age'],
  badges: ['Audiobooks Available'],
  joinedDate: '2023-03-20'
}, {
  slug: 'sarah-johnson',
  name: 'Sarah Johnson',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Horror and supernatural fiction writer exploring the depths of human fear and the unknown.',
  location: 'Boston, MA',
  genres: ['Horror', 'Supernatural', 'Dark Fantasy'],
  bookCount: 15,
  rating: 4.8,
  reviewCount: 420,
  hook: 'Atmospheric horror exploring the depths of human fear and the supernatural.',
  readerExperience: ['Atmospheric Dread', 'Supernatural Elements', 'Psychological Horror'],
  badges: ['Completed Series'],
  joinedDate: '2022-11-10'
}];
// Featured authors data keyed by featured type
const featuredAuthorsMap: Record<string, {
  title: string;
  description: string;
  gradient: string;
  authors: Author[];
}> = {
  'author-spotlight': {
    title: 'New Author Spotlight',
    description: "This month's featured indie authors handpicked by the LitLaunch community",
    gradient: 'from-amber-500 to-orange-500',
    authors: [{
      slug: 'amara-osei',
      name: 'Amara Osei',
      avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Debut author of sweeping historical romance set in West Africa. Her first novel has already garnered critical acclaim.',
      location: 'Atlanta, GA',
      genres: ['Historical Romance', 'Literary Fiction'],
      bookCount: 2,
      rating: 4.9
    }, {
      slug: 'kai-nakamura',
      name: 'Kai Nakamura',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Speculative fiction writer blending Japanese mythology with cyberpunk aesthetics. Rising star in indie sci-fi.',
      location: 'San Francisco, CA',
      genres: ['Science Fiction', 'Cyberpunk', 'Mythology'],
      bookCount: 3,
      rating: 4.6
    }, {
      slug: 'luna-reyes',
      name: 'Luna Reyes',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Magical realism author whose debut novel became a surprise bestseller. Writes stories rooted in Latinx culture.',
      location: 'Austin, TX',
      genres: ['Magical Realism', 'Contemporary Fiction'],
      bookCount: 1,
      rating: 4.8
    }, {
      slug: 'devon-blake',
      name: 'Devon Blake',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Cozy mystery writer with a knack for quirky small-town settings and lovable amateur sleuths.',
      location: 'Savannah, GA',
      genres: ['Cozy Mystery', 'Humor'],
      bookCount: 4,
      rating: 4.5
    }, {
      slug: 'priya-sharma',
      name: 'Priya Sharma',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Dark fantasy author weaving South Asian folklore into gripping, atmospheric tales of power and sacrifice.',
      location: 'Chicago, IL',
      genres: ['Dark Fantasy', 'Mythology', 'Horror'],
      bookCount: 5,
      rating: 4.7
    }]
  },
  'indie-spotlight': {
    title: 'Indie Author Spotlight',
    description: 'Connect directly with independent authors making waves in the literary world',
    gradient: 'from-indigo-500 to-blue-500',
    authors: [{
      slug: 'elena-richards',
      name: 'Elena Richards',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Award-winning author of psychological thrillers and mystery novels. Known for intricate plots and complex female protagonists.',
      location: 'Seattle, WA',
      genres: ['Mystery', 'Thriller', 'Psychological Suspense'],
      bookCount: 12,
      rating: 4.7
    }, {
      slug: 'marcus-chen',
      name: 'Marcus Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Fantasy and young adult fiction author, creating immersive worlds filled with magic and adventure.',
      location: 'Portland, OR',
      genres: ['Fantasy', 'Young Adult', 'Urban Fantasy'],
      bookCount: 8,
      rating: 4.5
    }, {
      slug: 'sarah-johnson',
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Horror and supernatural fiction writer exploring the depths of human fear and the unknown.',
      location: 'Boston, MA',
      genres: ['Horror', 'Supernatural', 'Dark Fantasy'],
      bookCount: 15,
      rating: 4.8
    }]
  }
};
const FEATURED_PREVIEW_COUNT = 5;
// Derive all unique genres from authors and featured authors
const getAllGenres = () => {
  const allAuthors = [...authors, ...Object.values(featuredAuthorsMap).flatMap(d => d.authors)];
  const genres = new Set<string>();
  allAuthors.forEach(author => {
    author.genres.forEach(genre => genres.add(genre));
  });
  return Array.from(genres).sort();
};
const allGenres = getAllGenres();
const sortOptions = [{
  value: 'name-asc',
  label: 'A-Z'
}, {
  value: 'name-desc',
  label: 'Z-A'
}, {
  value: 'books-desc',
  label: 'Most Books'
}, {
  value: 'rating-desc',
  label: 'Highest Rated'
}];
const ratingOptions = [{
  value: '4.5',
  label: '4.5+ Stars'
}, {
  value: '4.0',
  label: '4.0+ Stars'
}, {
  value: '3.5',
  label: '3.5+ Stars'
}];
export default function Authors() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [showAllFeatured, setShowAllFeatured] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name-asc');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const featuredKey = searchParams.get('featured');
  const featuredData = featuredKey ? featuredAuthorsMap[featuredKey] : null;
  const dismissFeatured = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('featured');
    setSearchParams(newParams);
    setShowAllFeatured(false);
  };
  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRatings([]);
    setSortBy('name-asc');
    setSearchQuery('');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: sortOptions,
    value: sortBy,
    onChange: val => setSortBy(val)
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: allGenres.map(g => ({
      value: g,
      label: g
    })),
    value: selectedGenres,
    onChange: val => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]);
    }
  }, {
    id: 'ratings',
    title: 'Rating',
    type: 'checkbox',
    options: ratingOptions,
    value: selectedRatings,
    onChange: val => {
      setSelectedRatings(prev => prev.includes(val) ? prev.filter(r => r !== val) : [...prev, val]);
    }
  }];
  const getFilteredAuthors = () => {
    let result = [...authors];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(author => author.name.toLowerCase().includes(query) || author.genres.some(genre => genre.toLowerCase().includes(query)));
    }
    if (selectedGenres.length > 0) {
      result = result.filter(author => author.genres.some(genre => selectedGenres.includes(genre)));
    }
    if (selectedRatings.length > 0) {
      const minRating = Math.min(...selectedRatings.map(parseFloat));
      result = result.filter(author => author.rating >= minRating);
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'books-desc':
          return b.bookCount - a.bookCount;
        case 'rating-desc':
          return b.rating - a.rating;
        default:
          return 0;
      }
    });
    return result;
  };
  const filteredAuthors = getFilteredAuthors();
  const displayedFeaturedAuthors = featuredData ? showAllFeatured ? featuredData.authors : featuredData.authors.slice(0, FEATURED_PREVIEW_COUNT) : [];
  return <div className={`min-h-screen ${dk.page} ${dk.textPrimary}`} data-id="element-6534" data-mp="3vceh7okz5xg2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6535" data-mp="3gotcxrcd5s6k">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-6536" data-mp="3lg1oasx97nmd">
          <h1 className={`text-3xl font-bold mb-4 ${dk.textHeading}`} data-id="element-6537" data-mp="3lh30j0q0ag5o">
            Discover Authors
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-6538" data-mp="37mk3l4su04ur">
            Explore our community of independent authors and find your next
            favorite storyteller.
          </p>

          <div className="mt-6 max-w-xl mx-auto flex items-center gap-4" data-id="element-6539" data-mp="2xsjs8l6yi3dl">
            <div className="relative flex-1" data-id="element-6540" data-mp="32osai7ggacoz">
              <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${dk.textMuted}`} data-id="element-6541" data-mp="2j38ksilwj8og" />
              <input type="text" placeholder="Search by author name or genre..." className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6542" data-mp="2f387j1ajh3iz" />
            </div>
            <div className={`hidden sm:flex items-center border rounded-lg p-1 ${dk.card} ${dk.border}`} data-id="element-6543" data-mp="2y1kjeusu4ri9">
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? `${rt.bgDark} text-white` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} aria-label="List view" data-id="element-6544" data-mp="3bre91fc5zft1">
                <LayoutListIcon className="h-5 w-5" data-id="element-6545" data-mp="3gsk5no8y3n7u" />
              </button>
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? `${rt.bgDark} text-white` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} aria-label="Grid view" data-id="element-6546" data-mp="1e5de0kqci3e9">
                <LayoutGridIcon className="h-5 w-5" data-id="element-6547" data-mp="1su14wdp8xoxv" />
              </button>
            </div>
          </div>

          <div className="mt-4 max-w-xl mx-auto flex gap-2 lg:hidden" data-id="element-6548" data-mp="v6j0tg6j5b07">
            <button className={`flex-1 flex items-center justify-center px-4 py-2 border rounded-lg shadow-sm ${dk.card} ${dk.textSecondary} ${dk.border} ${dk.dropdownItemHover}`} onClick={toggleMobileFilters} data-id="element-6549" data-mp="104ub1s0qlniy">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-6550" data-mp="1a4l4dqdk9whg" />
              Filters & Sort
            </button>
            <div className={`flex items-center border rounded-lg p-1 ${dk.card} ${dk.border}`} data-id="element-6551" data-mp="2gikd6t8aq1py">
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? `${rt.bgDark} text-white` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} data-id="element-6552" data-mp="2lk9om9zfkhek">
                <LayoutListIcon className="h-5 w-5" data-id="element-6553" data-mp="48pexlje7qs" />
              </button>
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? `${rt.bgDark} text-white` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} data-id="element-6554" data-mp="3rbl332n7o31y">
                <LayoutGridIcon className="h-5 w-5" data-id="element-6555" data-mp="3i925awya8gmk" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-6556" data-mp="3ddmu6crnsytu">
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={toggleMobileFilters} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-6557" data-mp="38dh7zc45s4j5">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-6558" data-mp="16lm0l8oxqm6q" />
          </FilterSidebarWrapper>

          <div className="flex-1 min-w-0" data-id="element-6559" data-mp="30zzwt26tlva5">
            {/* Featured Section */}
            {featuredData && <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-500" data-id="element-6560" data-mp="35u3zvacytj5y">
                <div className={`relative bg-gradient-to-r ${featuredData.gradient} rounded-lg px-4 py-3 sm:px-5 sm:py-4 mb-4 text-white overflow-hidden`} data-id="element-6561" data-mp="2r10k98ixpfhv">
                  <div className="flex items-center justify-between gap-3" data-id="element-6562" data-mp="3jsk7d3injdp5">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-6563" data-mp="3opkg5m6vyujy">
                      <SparklesIcon className="h-4 w-4 flex-shrink-0 opacity-80" data-id="element-6564" data-mp="39u4i7636qeia" />
                      <div className="min-w-0" data-id="element-6565" data-mp="3espl5d9tncaf">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-6566" data-mp="3erehrekag6ju">
                          <h2 className="text-base sm:text-lg font-bold truncate" data-id="element-6567" data-mp="1ye6bv9ze66kr">
                            {featuredData.title}
                          </h2>
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-6568" data-mp="lfxh7h90jjgv">
                            {featuredData.authors.length} author
                            {featuredData.authors.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <p className="text-white/75 text-xs sm:text-sm mt-0.5 line-clamp-1" data-id="element-6569" data-mp="qfa2hxk8kihc">
                          {featuredData.description}
                        </p>
                      </div>
                    </div>
                    <button onClick={dismissFeatured} className="flex-shrink-0 p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Dismiss featured section" data-id="element-6570" data-mp="3twilikqp9odn">
                      <XIcon className="h-4 w-4" data-id="element-6571" data-mp="3f6l1mgzszku9" />
                    </button>
                  </div>
                </div>
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6' : 'grid-cols-1 gap-4'}`} data-id="element-6572" data-mp="3b612x8zl0960">
                  {displayedFeaturedAuthors.map(author => {
                const profileData = mapAuthorToProfileCard({
                  ...author,
                  featured: true
                });
                return <div key={author.slug} className="relative h-full" data-id="element-6573" data-mp="hv71zn4cghhg">
                        <div className="absolute -top-2 -left-2 z-10" data-id="element-6574" data-mp="mua3wyp127l7">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${featuredData.gradient} shadow-md`} data-id="element-6575" data-mp="rsle5aj8ik3y">
                            <SparklesIcon className="h-3 w-3" data-id="element-6576" data-mp="10jlpi0fgdddi" />
                            Featured
                          </span>
                        </div>
                        <ProfileCard data={profileData} viewMode={viewMode} data-id="element-6577" data-mp="1aitclfbzomdt" />
                      </div>;
              })}
                </div>
                {featuredData.authors.length > FEATURED_PREVIEW_COUNT && <div className="mt-4 text-center" data-id="element-6578" data-mp="1fn56i35skgk0">
                    <button onClick={() => setShowAllFeatured(!showAllFeatured)} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors bg-gradient-to-r ${featuredData.gradient} text-white hover:opacity-90 shadow-sm`} data-id="element-6579" data-mp="1aok725yd60wm">
                      {showAllFeatured ? <>Show Less</> : <>
                          View All {featuredData.authors.length} Featured
                          Authors
                          <ChevronRightIcon className="h-4 w-4" data-id="element-6580" data-mp="2m3z4fauhius9" />
                        </>}
                    </button>
                  </div>}
                <div className={`mt-8 mb-6 border-t ${dk.divider}`} data-id="element-6581" data-mp="2hb618renzuia" />
              </div>}

            {/* All Authors Grid */}
            <div className="mb-6" data-id="element-6582" data-mp="7qlr33la4cvf">
              <h3 className={`text-lg font-semibold mb-4 ${dk.textHeading}`} data-id="element-6583" data-mp="7pk4q2ytc3ye">
                All Authors
              </h3>
              <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6' : 'grid-cols-1 gap-4'}`} data-id="element-6584" data-mp="2u4vjobr2w8b">
                {filteredAuthors.length > 0 ? filteredAuthors.map(author => {
                const profileData = mapAuthorToProfileCard(author);
                return <div key={author.slug} className="relative h-full" data-id="element-6585" data-mp="lpley90ywnqj">
                        {author.isDebut !== undefined && <div className="absolute top-2 right-2 z-10" data-id="element-6586" data-mp="gpz240p1x9i9">
                            {author.isDebut ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-pink-100 text-pink-800 border border-pink-200" data-id="element-6587" data-mp="19gqsuincl5y7">
                                Debut
                              </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-200" data-id="element-6588" data-mp="1lsjhree3q9up">
                                New
                              </span>}
                          </div>}
                        <ProfileCard data={profileData} viewMode={viewMode} data-id="element-6589" data-mp="24p2k9kuegzia" />
                      </div>;
              }) : <div className={`col-span-full text-center py-12 rounded-xl shadow-sm ${dk.card}`} data-id="element-6590" data-mp="29idrhn34jiaq">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${dk.surfaceMuted}`} data-id="element-6591" data-mp="2jf9sz8ulv45s">
                      <SearchIcon className={`h-6 w-6 ${dk.textMuted}`} data-id="element-6592" data-mp="obbtzz9pbi6n" />
                    </div>
                    <h3 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-6593" data-mp="jc7lzbyijqhw">
                      No authors found
                    </h3>
                    <p className={`mt-1 ${dk.textSecondary}`} data-id="element-6594" data-mp="x4npsez8nsjv">
                      Try adjusting your filters or search terms
                    </p>
                    <button onClick={resetFilters} className={`mt-4 font-medium ${rt.accentLink}`} data-id="element-6595" data-mp="sb2tcrdan6ms">
                      Clear all filters
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}